import {
  IAttribute,
  IComponent,
  IOption,
  ISlotInfoAdd,
  ISlotInfoRemove,
  ISlotsForType
} from '../Interface/Interface';

const getSlotAttributeValue = (
  slotAttributeIdentifier: string,
  option?: IOption
) =>
  option?.attributes?.find(
    ({ identifier }) => identifier === slotAttributeIdentifier
  )?.values[0].value || 0;

const provideSlotTypeAttributesFilter = ({ identifier }: IAttribute) =>
  identifier.startsWith('provides_slottype_');

const consumeSlotTypeAttributesFilter = ({ identifier }: IAttribute) =>
  identifier.startsWith('consumes_slottype_');

const getProvideSlotDataFromAttribute = (attribute: IAttribute) => {
  const { identifier, translated_title, values } = attribute;
  const slotsProvided = values[0].value;
  const matches = identifier.match(/(.+)_([\d]+)cpu$/);
  const baseIdentifier = matches?.[1] || identifier;
  const cpus = (matches?.[2] && parseInt(matches[2])) || 0;
  const consumeType = baseIdentifier.replace('provides_', 'consumes_');
  return {
    identifier: baseIdentifier,
    title: translated_title,
    slotsProvided,
    cpus,
    consumeType
  };
};

const getConsumeSlotDataFromAttribute = (attribute: IAttribute) => {
  const { identifier, translated_title, values } = attribute;
  const slotsNeeded = values[0].value;
  return {
    identifier,
    title: translated_title,
    slotsNeeded
  };
};

const getCPUAmountDependentSlotInfoForOption = (
  option: IOption,
  cpuAmount: number,
  itemAttributes: IAttribute[],
  availableSlotsForType: ISlotsForType
) => {
  const slotsProvidedByCPU = itemAttributes
    .filter(provideSlotTypeAttributesFilter)
    .map((attribute) => {
      const slotData = getProvideSlotDataFromAttribute(attribute);
      const availableSlots = availableSlotsForType[slotData.consumeType];
      return {
        isActive: slotData.cpus === cpuAmount,
        availableSlots,
        ...slotData
      };
    })
    .sort((a, b) => a.cpus - b.cpus);
  const activeSlots = slotsProvidedByCPU
    .filter(({ isActive }) => isActive)
    .map((activeSlot) => {
      const slotsNeeded = activeSlot.slotsProvided - activeSlot.availableSlots;
      return {
        ...activeSlot,
        slotsNeeded
      };
    });
  if (!activeSlots.length) {
    return {
      slotInfoAdd: undefined,
      slotInfoRemove: undefined
    };
  }
  let activeSlotsForMinSlotInfo = activeSlots.filter(
    ({ slotsNeeded }) => slotsNeeded > 0
  );
  if (!activeSlotsForMinSlotInfo.length) {
    activeSlotsForMinSlotInfo = activeSlots;
  }
  const minSlotInfoBySlot = activeSlotsForMinSlotInfo.map((activeSlot) => {
    return (
      slotsProvidedByCPU
        .filter(({ identifier }) => identifier === activeSlot.identifier)
        .find(({ slotsProvided }) => slotsProvided >= activeSlot.slotsNeeded) ||
      activeSlot
    );
  });
  const minSlotInfo = minSlotInfoBySlot.reduce(
    (result, slotInfo) =>
      (!result && slotInfo) || (result.cpus > slotInfo.cpus ? result : slotInfo)
  );

  let slotInfoRemove: ISlotInfoRemove | undefined;
  let slotInfoAdd: ISlotInfoAdd | undefined;

  for (const { identifier, title, isActive } of minSlotInfoBySlot) {
    if (isActive) {
      if (!slotInfoRemove) {
        slotInfoRemove = {
          slots: [],
          slotsProvided: minSlotInfo.slotsProvided,
          availableSlots: minSlotInfo.availableSlots,
          amountCanBeDecreased: false,
          minAmount: minSlotInfo.cpus
        };
      }
      slotInfoRemove.slots?.push({ identifier, title });
    }
  }
  if (!slotInfoRemove && minSlotInfo.cpus < cpuAmount) {
    slotInfoRemove = {
      minAmount: minSlotInfo.cpus
    };
  }

  const slotsNeededForCPU = option.attributes
    .filter(consumeSlotTypeAttributesFilter)
    .map(getConsumeSlotDataFromAttribute);
  for (const { identifier, title, slotsNeeded } of slotsNeededForCPU) {
    const availableSlots = availableSlotsForType[identifier];
    if (availableSlots < slotsNeeded) {
      if (!slotInfoAdd?.slots) {
        slotInfoAdd = {
          slots: [],
          slotsNeeded,
          availableSlots,
          amountCanBeIncreased: false
        };
      }
      slotInfoAdd.slots?.push({ identifier, title });
    }
  }

  return {
    slotInfoAdd,
    slotInfoRemove
  };
};

export const createSlotInfoForOption = (
  option: IOption,
  availableSlotsForType: ISlotsForType,
  component: IComponent | undefined,
  cpuAmount: number,
  itemAttributes: IAttribute[]
) => {
  if (component?.identifier === 'cpu' && itemAttributes.length) {
    return getCPUAmountDependentSlotInfoForOption(
      option,
      cpuAmount,
      itemAttributes,
      availableSlotsForType
    );
  }

  const isMultiSelect = component?.is_multiselect;
  const isSingleSelect = !isMultiSelect;
  const selectedOption = component?.selectedoptions?.[0];
  const singleSelectAmountIsSelectable = selectedOption?.amount_is_selectable;

  const slotsNeededForOption = option.attributes
    .filter(consumeSlotTypeAttributesFilter)
    .map(getConsumeSlotDataFromAttribute);
  const slotsProvidedByOption = option.attributes
    .filter(provideSlotTypeAttributesFilter)
    .map(getProvideSlotDataFromAttribute)
    .filter(({ cpus }) => !cpus || cpus === cpuAmount);

  let slotInfoAdd: ISlotInfoAdd | undefined;
  let slotInfoRemove: ISlotInfoRemove | undefined;

  if (
    isSingleSelect &&
    selectedOption?.identifier &&
    selectedOption.identifier !== option.identifier
  ) {
    // 1. check option switching in a single select component
    const nextAmount = option.amount_is_selectable ? selectedOption.amount : 1;

    // 1/a. switching would lead to insufficient required slots
    for (const { identifier, title, slotsNeeded } of slotsNeededForOption) {
      const availableSlots = availableSlotsForType[identifier];
      const currentSlotsNeeded =
        getSlotAttributeValue(identifier, selectedOption) *
        selectedOption.amount;
      const nextAvailableSlots = availableSlots + currentSlotsNeeded;
      const nextSlotsNeeded = slotsNeeded * nextAmount;
      if (nextSlotsNeeded > nextAvailableSlots) {
        if (!slotInfoAdd) {
          slotInfoAdd = {
            slots: [],
            slotsNeeded: nextSlotsNeeded,
            availableSlots: nextAvailableSlots,
            optionCanBeSwitched: false
          };
        }
        slotInfoAdd.slots?.push({ identifier, title });
      }
    }

    // 1/b. switching would lead to insufficient provided slots
    const slotsProvidedBySelectedOption = selectedOption.attributes
      .filter(provideSlotTypeAttributesFilter)
      .map(getProvideSlotDataFromAttribute);
    for (const { identifier, title, consumeType } of [
      ...slotsProvidedByOption,
      ...slotsProvidedBySelectedOption
    ]) {
      const availableSlots = availableSlotsForType[consumeType];
      const currentSlotsProvided =
        getSlotAttributeValue(identifier, selectedOption) *
        selectedOption.amount;
      const nextSlotsProvided =
        getSlotAttributeValue(identifier, option) * nextAmount;
      const nextAvailableSlots =
        availableSlots - currentSlotsProvided + nextSlotsProvided;
      if (nextAvailableSlots < 0) {
        if (!slotInfoRemove) {
          slotInfoRemove = {
            slots: [],
            slotsProvided: nextSlotsProvided,
            availableSlots: nextAvailableSlots,
            optionCanBeSwitched: false
          };
        }
        slotInfoRemove.slots?.push({ identifier, title });
      }
    }
  } else if (isMultiSelect || singleSelectAmountIsSelectable) {
    // 2. check option switching in a multiselect component,
    //    or when the option is already selected in a single select
    const configurationOption = component?.selectedoptions?.find(
      ({ identifier }) => identifier === option.identifier
    );
    const currentAmount = configurationOption?.amount || 0;
    const optionIsSelected = currentAmount > 0;

    const optionIsSelectedMultiSelectSingleAmount =
      optionIsSelected &&
      isMultiSelect &&
      !configurationOption?.amount_is_selectable;

    // 2/a. selecting an additional option or increasing the amount of any selected option
    //      would lead to insufficient required slots
    if (!optionIsSelectedMultiSelectSingleAmount) {
      for (const { identifier, title, slotsNeeded } of slotsNeededForOption) {
        const availableSlots = availableSlotsForType[identifier];
        if (availableSlots < slotsNeeded) {
          if (!slotInfoAdd?.slots) {
            slotInfoAdd = {
              slots: [],
              slotsNeeded,
              availableSlots,
              amountCanBeIncreased: currentAmount === 0,
              optionCanBeSwitched: optionIsSelected
            };
          }
          slotInfoAdd.slots?.push({ identifier, title });
        } else if (!slotInfoAdd?.slots) {
          const maxAmount =
            currentAmount + Math.floor(availableSlots / slotsNeeded);
          slotInfoAdd = {
            maxAmount: slotInfoAdd
              ? Math.min(slotInfoAdd.maxAmount || maxAmount, maxAmount)
              : maxAmount
          };
        }
      }
    }

    // 2/b. removing an option or decreasing the amount of an option
    //      would lead to insufficient provided slots
    if (optionIsSelected) {
      for (const {
        identifier,
        title,
        slotsProvided,
        consumeType
      } of slotsProvidedByOption) {
        const availableSlots = availableSlotsForType[consumeType];
        if (availableSlots - slotsProvided < 0) {
          if (!slotInfoRemove?.slots) {
            slotInfoRemove = {
              slots: [],
              slotsProvided,
              availableSlots,
              amountCanBeDecreased: false
            };
          }
          slotInfoRemove.slots?.push({ identifier, title });
        } else if (!slotInfoRemove?.slots) {
          const minAmount =
            currentAmount - Math.floor(availableSlots / slotsProvided);
          slotInfoRemove = {
            minAmount: slotInfoRemove
              ? Math.max(slotInfoRemove.minAmount || minAmount, minAmount)
              : minAmount
          };
        }
      }
    }
  }
  return {
    slotInfoAdd,
    slotInfoRemove
  };
};
